import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Spinner, Form } from "react-bootstrap";
import Dashboardheader from "../../directives/dashboardheader";
import {
	getWithdrawProposaldataAction,
	getWithdrawProposaldataByMasterAction,
	withdrawProposalRequestAction,
} from "../../Action/user.action";
import toast, { Toaster } from "react-hot-toast";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import Select from "react-select";
import CopyToClipboard from "react-copy-to-clipboard";


const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 300, 500];

const BusinessProposal = () => {
	// ------------------------- State ---------------------------------
	const [withdrawRequestList, setWithdrawRequestList] = useState([]);
	const [mastersList, setMastersList] = useState([]);
	const [walletTypeList, setWalletTypeList] = useState([]);
	const [dashboarddata, setDashboardData] = useState({});

	const [loading, setLoading] = useState(true);
	const [statisticsLoading, setStatisticsLoading] = useState(false);

	// UI controls / filters
	const [selectedMaster, setSelectedMaster] = useState(null);
	const [selectedWalletType, setSelectedWalletType] = useState(0);
	const [searchInput, setSearchInput] = useState("");
	const [filterOption, setFilterOption] = useState("");
	const [menterRemainingBalance, setMenterRemainingBalance] = useState(0);
	// pagination
	const [page, setPage] = useState(0); // zero-indexed internally
	const [rowsPerPage, setRowsPerPage] = useState(10);

	// selection
	const [selectedProposeRows, setSelectedProposeRows] = useState([]); // rows to propose (currently unproposed)
	const [selectedUnproposeRows, setSelectedUnproposeRows] = useState([]); // rows to unpropose (currently proposed)

	// derived balance values
	const masterRemainingBalance = useMemo(() => {
		const dash = dashboarddata || {};
		const totalBusiness = parseFloat(dash.totalBusiness || 0);
		const totalApproved = parseFloat(dash.totalApprovedProposed || 0);
		const totalCompanyCredit = parseFloat(dash.totalCompanyCredits || 0);
		const totalPending = parseFloat(dash.totalPendingProposed || 0);
		return Number(((totalBusiness + totalCompanyCredit) - (totalApproved + totalPending)) || 0);
	}, [dashboarddata]);

	// ------------------------- Effects ---------------------------------
	useEffect(() => {
		getWithdrawReqList();
	}, []);

	// ------------------------- API Calls ---------------------------------
	const getWithdrawReqList = async () => {
		setLoading(true);
		setStatisticsLoading(true);
		try {
			const res = await getWithdrawProposaldataAction();
			if (res.success) {
				const allData = res.data?.history || [];
				setWithdrawRequestList(allData);
				setDashboardData(res.data?.statistics || {});
				setMastersList(res.data?.mastersList || []);
				setWalletTypeList(res.data?.walletTypes || []);
				let dashData = res.data?.statistics || {};
				let masterRemaining = (parseFloat(dashData.totalBusiness) + parseFloat(dashData.totalCompanyCredits)) - (parseFloat(dashData.totalApprovedProposed) + parseFloat(dashData.totalPendingProposed));
				setMenterRemainingBalance(masterRemaining)
				setSelectedProposeRows([]);
				setSelectedUnproposeRows([]);
			}
		} catch (err) {
			console.error(err);
			toast.error("Failed to fetch data");
		} finally {
			setLoading(false);
			setStatisticsLoading(false);
		}
	};

	const filterRecordsByMaster = async (masterOption) => {
		if (!masterOption) {
			setSelectedMaster(null);
			return getWithdrawReqList();
		}
		setSelectedMaster(masterOption);
		setLoading(true);
		setStatisticsLoading(true);
		try {
			const res = await getWithdrawProposaldataByMasterAction(masterOption.value);
			if (res.success) {
				const data = res.data?.history || [];
				setWithdrawRequestList(data);
				setDashboardData(res.data?.statistics || {});
				setSelectedProposeRows([]);
				setSelectedUnproposeRows([]);
			}
		} catch (err) {
			console.error(err);
			toast.error("Failed to fetch master data");
		} finally {
			setLoading(false);
			setStatisticsLoading(false);
		}
	};

	// ------------------------- Memoized Filtering & Sorting -----------------
	const normalizedSearch = (searchInput || "").trim().toLowerCase();

	const filteredAndSorted = useMemo(() => {
		setSelectedProposeRows([]);
		setSelectedUnproposeRows([]);
		let data = Array.isArray(withdrawRequestList) ? [...withdrawRequestList] : [];

		// Wallet type filter
		if (selectedWalletType && Number(selectedWalletType) > 0) {
			data = data.filter((d) => Number(d.withdraw_type) === Number(selectedWalletType));
		}

		// Search filter (email)
		if (normalizedSearch) {
			data = data.filter((d) => (d.email || "").toLowerCase().includes(normalizedSearch));
		}

		// Action filter (proposed/unproposed)
		if (filterOption === "proposed") {
			data = data.filter((d) => Number(d.is_proposed) === 1);
		} else if (filterOption === "unproposed") {
			data = data.filter((d) => Number(d.is_proposed) === 0);
		}

		// Sorting by date options
		if (filterOption === "date_newest") {
			data.sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));
		} else if (filterOption === "date_oldest") {
			data.sort((a, b) => new Date(a.requestDate) - new Date(b.requestDate));
		}

		// Filter by currency type
		if (filterOption === "ANANT") {
			data = data.filter((d) => d.box_currency_ids === "3" && !d.nft_purchased);
		}

		return data;
	}, [withdrawRequestList, selectedWalletType, normalizedSearch, filterOption]);

	// pagination slice
	const pagedData = useMemo(() => {
		const start = page * rowsPerPage;
		return filteredAndSorted.slice(start, start + rowsPerPage);
	}, [filteredAndSorted, page, rowsPerPage]);

	// ------------------------- Selection helpers ----------------------------
	const toggleSelect = (id, forPropose) => {
		if (forPropose) {
			setSelectedProposeRows((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
		} else {
			setSelectedUnproposeRows((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
		}
	};

	const selectAllVisible = (forPropose) => {
		const visibleIds = pagedData.filter((r) => (forPropose ? Number(r.is_proposed) === 0 : Number(r.is_proposed) === 1)).map((r) => r.id);
		if (forPropose) {
			const allSelected = visibleIds.every((id) => selectedProposeRows.includes(id)) && visibleIds.length > 0;
			if (allSelected) {
				// setSelectedProposeRows((prev) => prev.filter((id) => !visibleIds.includes(id)));
				setSelectedProposeRows([])
				setSelectedUnproposeRows([])
			} else setSelectedProposeRows((prev) => Array.from(new Set([...prev, ...visibleIds])));
		} else {
			const allSelected = visibleIds.every((id) => selectedUnproposeRows.includes(id)) && visibleIds.length > 0;
			if (allSelected) setSelectedUnproposeRows((prev) => prev.filter((id) => !visibleIds.includes(id)));
			else setSelectedUnproposeRows((prev) => Array.from(new Set([...prev, ...visibleIds])));
		}
	};

	const totalSelectedAmountToPropose = useMemo(() => {
		return withdrawRequestList
			.filter((r) => selectedProposeRows.includes(r.id))
			.reduce((s, r) => s + Number(r.amount || 0), 0);
	}, [withdrawRequestList, selectedProposeRows]);

	const totalSelectedAmountToUnPropose = useMemo(() => {
		return withdrawRequestList
			.filter((r) => selectedUnproposeRows.includes(r.id))
			.reduce((s, r) => s + Number(r.amount || 0), 0);
	}, [withdrawRequestList, selectedUnproposeRows]);

	const totalSelectedRowsAmount = totalSelectedAmountToPropose > 0 ? totalSelectedAmountToPropose : totalSelectedAmountToUnPropose;

	const isProposalLimitExceeded = totalSelectedAmountToPropose > menterRemainingBalance;

	// ------------------------- Action handlers ----------------------------
	const confirmAndSubmit = async ({ ids, is_proposed }) => {
		if (!ids || ids.length === 0) return toast.error("No rows selected");
		const message = is_proposed === 1 ? "You want to propose selected withdraw requests!" : "You want to unpropose selected withdraw requests!";
		const proceed = await Swal.fire({
			title: "Are you sure?",
			text: message,
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: "Yes, proceed!",
		});
		if (!proceed.isConfirmed) return;
		Swal.fire({
			title: "Please wait...",
			text: "Processing your request . Please do not close or refresh the page.",
			allowOutsideClick: false,
			didOpen: () => Swal.showLoading(),
		});
		try {
			const res = await withdrawProposalRequestAction({ withdraw_ids: ids, is_proposed });
			Swal.close();
			if (res.success) {
				toast.success(res.msg || "Proposal updated successfully");
				// clear selections for those ids
				if (is_proposed === 1) setSelectedProposeRows((prev) => prev.filter((id) => !ids.includes(id)));
				else setSelectedUnproposeRows((prev) => prev.filter((id) => !ids.includes(id)));
				await getWithdrawReqList();
			} else {
				toast.error(res.msg || "Something went wrong! Please try again.");
			}
		} catch (err) {
			Swal.close();
			console.error(err);
			toast.error("Something went wrong");
		}
	};

	const handleSingleToggle = async (row) => {
		// row present -> toggle for that row only
		if (!row) return;

		const is_proposed_value = Number(row.is_proposed) === 1 ? 0 : 1;
		// If proposing single row, ensure limit
		console.log("masterRemainingBalance", masterRemainingBalance);
		if (isProposalLimitExceeded) {
			await Swal.fire({ title: "Proposal Limit Exceeded", icon: "info", text: "Insufficient remaining balance." });
			return;
		}
		if (is_proposed_value === 1 && Number(row.amount || 0) > menterRemainingBalance) {
			await Swal.fire({ title: "Proposal Limit Exceeded", icon: "info", text: "Insufficient remaining balance." });
			return;
		}
		await confirmAndSubmit({ ids: [row.id], is_proposed: is_proposed_value });
	};

	const handleBulkPropose = async () => {
		if (selectedProposeRows.length === 0) return toast.error("Select at least one unproposed row to propose.");
		if (totalSelectedAmountToPropose > masterRemainingBalance) {
			await Swal.fire({ title: "Proposal Limit Exceeded", icon: "info", html: `Please select less than your remaining balance.` });
			return;
		}
		await confirmAndSubmit({ ids: selectedProposeRows, is_proposed: 1 });
	};

	const handleBulkUnpropose = async () => {
		if (selectedUnproposeRows.length === 0) return toast.error("Select at least one proposed row to unpropose.");
		// Only unpropose ids that are currently proposed
		const unproposeIds = withdrawRequestList.filter((r) => selectedUnproposeRows.includes(r.id) && Number(r.is_proposed) === 1).map((r) => r.id);
		if (unproposeIds.length === 0) return toast.error("No proposed rows selected to unpropose.");
		await confirmAndSubmit({ ids: unproposeIds, is_proposed: 0 });
	};

	// ------------------------- Table Columns --------------------------------
	const columns = [
		{
			name: "S.no",
			selector: (row, index) => index + 1 + (page * rowsPerPage),
			width: "5%"
		},
		{
			name: "Propose",
			cell: (row) => (
				<>
					{Number(row.is_proposed) === 1 ? (
						"-"
					) : (
						<input
							type="checkbox"
							checked={selectedProposeRows.includes(row.id)}
							onChange={() => toggleSelect(row.id, true)}
							disabled={Number(row.is_proposed) !== 0}
						/>
					)}
				</>
			),
			width: "80px",
		},
		{
			name: "Unpropose",
			cell: (row) => (
				<>
					{Number(row.is_proposed) === 0 ? (
						"-"
					) : (
						<input
							type="checkbox"
							checked={selectedUnproposeRows.includes(row.id)}
							onChange={() => toggleSelect(row.id, false)}
							disabled={Number(row.is_proposed) !== 1}
						/>
					)}
				</>
			),
			width: "100px",
		},
		{
			name: "Email",
			selector: (row) => row.email,
			cell: (row) => (
				<>
					{row.email}
					&nbsp;
					<CopyToClipboard text={row.email} onCopy={() => toast.success("Email copied!")}>
						<span title="Click to Copy" className="mr-copylink" style={{ cursor: "pointer", color: "rgb(157 81 255)" }}>
							<i className="fa fa-clone "></i>
						</span>
					</CopyToClipboard>
				</>
			),
			width: "280px",
		},
		{
			name: "Amount (ANANT)",
			selector: (row) => row.amount,
		},
		{ name: "Type", selector: (row) => row.withdraw_type_name },
		{ name: "Request Date", selector: (row) => row.requestDate },
		{ name: "Proposed Date", selector: (row) => (Number(row.is_proposed) === 1 ? row.proposed_date : "NA") },
		{
			name: "Action",
			cell: (row) => (
				<>
					{Number(row.is_proposed) === 0 ? (
						<button
							className="btn-main2 px-4"
							style={{ cursor: "pointer", minHeight: 35, fontSize: '14px', width: 200 }}
							onClick={() => handleSingleToggle(row)}
						>
							Propose
						</button>
					) : (
						<button className="btn btn-dark px-4 text-white" style={{ cursor: "pointer", minHeight: 35, fontSize: '14px', width: 200 }} onClick={() => handleSingleToggle(row)}>
							Unpropose
						</button>
					)}
				</>
			),
		},
	];

	const customStyles = {
		noData: { style: { display: "flex", alignItems: "center", justifyContent: "center", color: "white", backgroundColor: "transparent" } },
		headCells: { style: { paddingLeft: "16px", paddingRight: "16px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "16px" } },
		headRow: { style: { borderRadius: "10px 10px 0px 0px", border: "none", color: "white", textAlign: "center", background: "linear-gradient(174deg, rgba(65, 210, 255, 0.25) 0%, rgba(126, 66, 255, 0.25) 47.40%, rgba(221, 63, 255, 0.25) 96.19%)" } },
		cells: { style: { paddingLeft: "16px", paddingRight: "16px", wordBreak: "break-word", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "14px", margin: "10px 0px" } },
		table: { style: { color: "white", backgroundColor: "transparent" } },
		rows: { style: { fontSize: "13px", fontWeight: 400, color: "white", background: "linear-gradient(99deg, rgba(83, 207, 210, 0.20) 1.29%, rgba(92, 101, 225, 0.20) 49.43%, rgba(200, 33, 156, 0.20) 107.93%)", minHeight: "48px" } },
		pagination: { style: { fontSize: "13px", minHeight: "56px", background: "linear-gradient(99deg, rgba(83, 207, 210, 0.20) 1.29%, rgba(92, 101, 225, 0.20) 49.43%, rgba(200, 33, 156, 0.20) 107.93%)", borderBottom: "none", color: "white" }, pageButtonsStyle: { borderRadius: "50%", height: "40px", width: "40px", padding: "8px", cursor: "pointer", transition: "0.4s", fill: "white", backgroundColor: "transparent" } }
	};

	const visibleProposeIds = pagedData
		.filter(r => Number(r.is_proposed) === 0)
		.map(r => r.id);

	const visibleUnproposeIds = pagedData
		.filter(r => Number(r.is_proposed) === 1)
		.map(r => r.id);

	const allProposeChecked = visibleProposeIds.length > 0 &&
		visibleProposeIds.every(id => selectedProposeRows.includes(id));

	const allUnproposeChecked = visibleUnproposeIds.length > 0 &&
		visibleUnproposeIds.every(id => selectedUnproposeRows.includes(id));


	// ------------------------- Render ------------------------------------
	return (
		<>
			<Dashboardheader />
			<Toaster />
			<div className="position-relative">
				{/* <div className="db1" /> */}
				<div className="db4" />
				<div className="db5" />
				<div className="db6" />
				<div className="db7" />
				<div className="db8" />
				<div className="page-wrapper">
					<div className="position-relative overflow-hidden mt-5 pt-2 mb-5">
						{/* Stats Row */}
						<div className="mb-5">
							<div className="container referral">
								<div className="mr-referral-content">
									<div className="mr-referral-block">
										<h2 className="text-white sofia-sans" style={{ fontWeight: 600 }}>Withdraw Proposal</h2>
										<Row className="gy-3" style={{ scrollbarWidth: "none" }}>
											{[{
												value: statisticsLoading ? <Spinner animation="border" role="status" variant="primary" size="sm" /> : Number(dashboarddata.teamSize || 0).toFixed(0),
												label: 'Team',
											}, {
												value: statisticsLoading ? <Spinner animation="border" role="status" variant="primary" size="sm" /> : Number(dashboarddata.totalBusiness || 0).toFixed(2),
												label: 'Total ANANT'
											}, {
												value: statisticsLoading ? <Spinner animation="border" role="status" variant="primary" size="sm" /> : Number(dashboarddata.totalCompanyCredits || 0).toFixed(2),
												label: 'Company Business Credit'
											}, {
												value: statisticsLoading ? <Spinner animation="border" role="status" variant="primary" size="sm" /> : Number(dashboarddata.totalApprovedProposed || 0).toFixed(2),
												label: 'Approved Balance'
											}, {
												value: statisticsLoading ? <Spinner animation="border" role="status" variant="primary" size="sm" /> : Number(masterRemainingBalance || 0).toFixed(2),
												label: 'Remaining Balance'
											}, {
												value: statisticsLoading ? <Spinner animation="border" role="status" variant="primary" size="sm" /> : Number(dashboarddata.totalPendingProposed || 0).toFixed(2),
												label: 'Proposed Balance'
											}].map((card, idx) => (
												<Col className="" key={idx}>
													<div className="card card-view ">
														<div className="card-body text-center px-3 cst-pb">
															<div className="card-d d-flex flex-column justify-content-center align-items-center">
																<h3 className="sofia-sans">{card.value}</h3>
																<p className="mb-0  sofia-sans d-flex flex-column justify-content-center align-items-center card-height" >{card.label}</p>
															</div>
														</div>
													</div>
												</Col>

											))}
										</Row>
									</div>
								</div>
							</div>
						</div>

						{/* Filters & Actions */}
						<div className="latest_transaction mb-5">
							<Container>
								<Row style={{ scrollbarWidth: "none" }}>
									<Col lg={4} sm={6} className="mb-3">
										<div className="align-items-center gap-3 d-flex">
											{selectedUnproposeRows.length === 0 && (
												<button className="btn new-custom-button" onClick={() => selectAllVisible(true)}>
													{allProposeChecked
														? `Deselect All Propose (${selectedProposeRows.length})`
														: `Select All Propose`}
												</button>
											)}
											{selectedProposeRows.length === 0 && (
												<>
													&nbsp;
													<button className="btn new-custom-button" onClick={() => selectAllVisible(false)}>
														{allUnproposeChecked
															? `Deselect All Unpropose (${selectedUnproposeRows.length})`
															: `Select All Unpropose`}
													</button>
												</>
											)}
											{/* Bulk actions */}
											{selectedUnproposeRows.length === 0 && selectedProposeRows.length > 0 && (
												<>
													&nbsp;&nbsp;
													<button className="btn btn-primary" onClick={handleBulkPropose}>Bulk Propose</button>
												</>
											)}
											{selectedProposeRows.length === 0 && selectedUnproposeRows.length > 0 && (
												<>
													&nbsp;
													<button className="btn btn-primary" onClick={handleBulkUnpropose}>Bulk Unpropose</button>
												</>
											)}
										</div>
									</Col>

									<Col lg={3} sm={6} className="mb-3">
										<Select
											className="react-select sign-up-form"
											classNamePrefix="react-select"
											name="selectedMaster"
											value={selectedMaster}
											onChange={(e) => filterRecordsByMaster(e)}
											options={[...mastersList.map((m) => ({ value: m.id, label: m.email }))]}
											placeholder="Select Master"
											isSearchable
										/>
									</Col>

									<Col lg={2} sm={6} className="mb-3">
										<select className="form-select customnav-link" style={{ background: '#0b1e29', border: '1px solid #747687', color: 'gray' }} value={selectedWalletType} onChange={(e) => setSelectedWalletType(e.target.value)}>
											<option value={0}>Filter By Wallet</option>
											{walletTypeList.map((type) => (
												<option key={type.id} value={type.id}>{type.name}</option>
											))}
										</select>
									</Col>

									<Col lg={3} sm={6} className="mb-3">
										<input type="text" className="form-control" placeholder="Search by Email" style={{ height: '38px' }} onChange={(e) => setSearchInput(e.target.value)} />
									</Col>
									<Col lg={3} sm={12} className="mb-3 ms-auto text-end">
										<select className="form-select customnav-link" style={{ background: '#0b1e29', border: '1px solid #747687', color: 'gray' }} value={filterOption} onChange={(e) => setFilterOption(e.target.value)}>
											<option value={0}>Filter by Status / Date</option>

											<option value="date_newest">Newest First</option>
											<option value="date_oldest">Oldest First</option>
											<option value="proposed">Proposed</option>
											<option value="unproposed">Unproposed</option>
											<option value="ANANT">3.0(Only ANANT)</option>
										</select>
									</Col>

									<Col lg={4} sm={12} className="pl-5 mb-3 text-white d-flex align-items-center" style={{ fontSize: '14px' }}>
										Total Records : {filteredAndSorted.length}
									</Col>

									<Col lg={2} sm={12} className="pl-5 mb-3 text-white d-flex align-items-center" style={{ fontSize: '14px' }}>
										Total Selected : {selectedProposeRows.length + selectedUnproposeRows.length} rows
									</Col>

									<Col lg={3} sm={12} className="pl-5 mb-3 text-white d-flex align-items-center" style={{ fontSize: '14px' }}>
										Total Selected Amount : {totalSelectedRowsAmount.toFixed(2)} ANANT
									</Col>

								</Row>

								{loading ? (
									<div className="d-flex justify-content-center align-items-center mt-3" style={{ height: 50 }}>
										<Spinner animation="border" role="status" variant="primary">
											<span className="visually-hidden">Loading...</span>
										</Spinner>
									</div>
								) : (
									<DataTable
										columns={columns}
										data={pagedData}
										customStyles={customStyles}
										pagination
										paginationServer
										paginationTotalRows={filteredAndSorted.length}
										paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
										onChangePage={(p) => setPage(p - 1)}
										onChangeRowsPerPage={(r, p) => { setRowsPerPage(r); setPage(p - 1); }}
									/>
								)}
							</Container>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default BusinessProposal;
